import React, { useCallback, useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import AdminStoryService from "../../story/AdminStoryService";
import { ART_STYLE_OPTIONS, artStyleLabel } from "./artStyleOptions";

const PHOTO_ART_STYLE_KEYS = ["photorealistic", "cinematic"];
const PHOTO_ART_STYLE_OPTIONS = ART_STYLE_OPTIONS.filter((opt) =>
    PHOTO_ART_STYLE_KEYS.includes(opt.key),
);

/** Default story for prompt experiments. */
export const TEST_PROMPT_V2_STORY_ID = "92f220b8-01e5-4fef-9bc8-dc578719f33f";

const HISTORY_STORAGE_KEY = "admin.testPromptV2.history.v1";
const HISTORY_MAX = 40;

function readHistory() {
    try {
        const raw = window.localStorage.getItem(HISTORY_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function writeHistory(entries) {
    try {
        window.localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(entries.slice(0, HISTORY_MAX)));
    } catch {
        // ignore quota / private mode
    }
}

function pickTrimmedPrompt(obj) {
    if (!obj || typeof obj !== "object") return "";
    const a = obj.imagePrompt;
    const b = obj.image_prompt;
    if (typeof a === "string" && a.trim()) return a.trim();
    if (typeof b === "string" && b.trim()) return b.trim();
    return "";
}

function normalizePromptVersion(raw) {
    if (raw === "v1") return "v1";
    if (raw === "photo") return "photo";
    if (raw === "standalone-v2" || raw === "standalone_v2") return "standalone-v2";
    return "v2";
}

function pickImageUrl(obj) {
    if (!obj || typeof obj !== "object") return null;
    const a = obj.imageUrl ?? obj.image_url;
    if (typeof a === "string" && a.trim()) return a.trim();
    return null;
}

function ComparePane({ label, url, emptyHint }) {
    return (
        <Box
            sx={{
                flex: "1 1 320px",
                minWidth: 280,
                maxWidth: 560,
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {label}
            </Typography>
            <Box
                sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    minHeight: 280,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    p: 1,
                }}
            >
                {url ? (
                    <Box
                        component="img"
                        src={url}
                        alt={label}
                        sx={{
                            width: "100%",
                            height: "auto",
                            maxHeight: 480,
                            objectFit: "contain",
                            display: "block",
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, textAlign: "center" }}>
                        {emptyHint}
                    </Typography>
                )}
            </Box>
            {url ? (
                <Button
                    size="small"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    sx={{ mt: 1, textTransform: "none" }}
                >
                    Open image
                </Button>
            ) : null}
        </Box>
    );
}

/**
 * Admin Anything tool — regenerate a story illustration into story-revision/ and compare.
 * Supports promptVersion v1 (legacy), v2 (hybrid + theme pack), and photo (word-puzzle photo wrap).
 */
export default function TestPromptV2Main({
    defaultPromptVersion = "v2",
    pageTitle = "Test new prompt v2",
    pageSubtitle,
} = {}) {
    const navigate = useNavigate();
    const [storyId, setStoryId] = useState(TEST_PROMPT_V2_STORY_ID);
    const [prompt, setPrompt] = useState("");
    const [promptVersion, setPromptVersion] = useState(() => normalizePromptVersion(defaultPromptVersion));
    const [imageModel, setImageModel] = useState("core");
    const [artStyle, setArtStyle] = useState(
        defaultPromptVersion === "photo" ? "photorealistic" : "none",
    );
    const [artStyleTail, setArtStyleTail] = useState(null);
    const [useEditedPrompt, setUseEditedPrompt] = useState(false);
    const [currentImageUrl, setCurrentImageUrl] = useState(null);
    const [revisionImageUrl, setRevisionImageUrl] = useState(null);
    const [revisionS3Key, setRevisionS3Key] = useState(null);
    const [themeKey, setThemeKey] = useState(null);
    const [themeStyleTail, setThemeStyleTail] = useState(null);
    const [storyTags, setStoryTags] = useState([]);
    const [loadingStory, setLoadingStory] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);
    const [history, setHistory] = useState(() => readHistory());

    const loadStory = useCallback(async (id) => {
        const trimmed = String(id || "").trim();
        if (!trimmed) {
            setError("Enter a story id.");
            return;
        }
        setLoadingStory(true);
        setError(null);
        setInfo(null);
        setRevisionImageUrl(null);
        setRevisionS3Key(null);
        setThemeKey(null);
        setThemeStyleTail(null);
        setStoryTags([]);
        const result = await AdminStoryService.getById(trimmed);
        setLoadingStory(false);
        if (!result.success || !result.data) {
            setCurrentImageUrl(null);
            setPrompt("");
            setError(result.error || "Story not found");
            return;
        }
        const story = result.data;
        setPrompt(pickTrimmedPrompt(story));
        setCurrentImageUrl(pickImageUrl(story));
        setUseEditedPrompt(false);
        setInfo(`Loaded story ${trimmed}`);
    }, []);

    useEffect(() => {
        loadStory(TEST_PROMPT_V2_STORY_ID);
    }, [loadStory]);

    const handleGenerate = async () => {
        const id = storyId.trim();
        if (!id) {
            setError("Enter a story id.");
            return;
        }
        if (useEditedPrompt && !prompt.trim()) {
            setError("Prompt is empty — paste a prompt or turn off 'Use edited prompt'.");
            return;
        }
        setGenerating(true);
        setError(null);
        setInfo(null);
        const promptArg = useEditedPrompt ? prompt : "";
        const styleArg =
            promptVersion === "photo" && !PHOTO_ART_STYLE_KEYS.includes(artStyle)
                ? "photorealistic"
                : artStyle;
        const result = await AdminStoryService.testPromptV2(
            id,
            promptArg,
            promptVersion,
            imageModel,
            styleArg,
        );
        setGenerating(false);
        if (!result.success) {
            setError(result.error || "Generation failed");
            return;
        }
        const data = result.data || {};
        const revision = data.revisionImageUrl || data.revision_image_url || null;
        const current = data.currentImageUrl || data.current_image_url || currentImageUrl;
        setRevisionImageUrl(revision);
        setRevisionS3Key(data.s3Key || data.s3_key || null);
        if (current) setCurrentImageUrl(current);
        if (typeof data.prompt === "string" && data.prompt.trim()) {
            setPrompt(data.prompt.trim());
        }
        const nextTheme = data.themeKey || data.theme_key || null;
        setThemeKey(nextTheme);
        setThemeStyleTail(data.themeStyleTail || data.theme_style_tail || null);
        const nextArtStyle = data.artStyle || data.art_style || artStyle;
        if (typeof nextArtStyle === "string" && nextArtStyle.trim()) {
            setArtStyle(nextArtStyle.trim());
        }
        setArtStyleTail(data.artStyleTail || data.art_style_tail || null);
        const tags = data.tags;
        setStoryTags(Array.isArray(tags) ? tags : []);
        const ver = data.promptVersion || data.prompt_version || promptVersion;
        const model = data.imageModel || data.image_model || imageModel;
        if (model === "core" || model === "ultra") setImageModel(model);
        const s3Key = data.s3Key || data.s3_key || null;
        const styleLabel = artStyleLabel(nextArtStyle);
        setInfo(
            revision
                ? `Revision saved under story-revision/ (${ver}, ${model}, style=${styleLabel}${nextTheme ? `, theme=${nextTheme}` : ""}). Story row unchanged.`
                : "Request completed but no revision URL returned.",
        );
        if (revision) {
            const entry = {
                id: `${Date.now()}-${id}`,
                at: new Date().toISOString(),
                storyId: id,
                promptVersion: ver,
                imageModel: model,
                artStyle: nextArtStyle || "none",
                themeKey: nextTheme,
                tags: Array.isArray(tags) ? tags : [],
                currentImageUrl: current || null,
                revisionImageUrl: revision,
                s3Key,
                prompt: typeof data.prompt === "string" ? data.prompt : prompt,
            };
            setHistory((prev) => {
                const next = [entry, ...prev].slice(0, HISTORY_MAX);
                writeHistory(next);
                return next;
            });
        }
    };

    const handleOpenHistory = (entry) => {
        if (!entry) return;
        setStoryId(entry.storyId || "");
        setPrompt(entry.prompt || "");
        setPromptVersion(normalizePromptVersion(entry.promptVersion));
        setImageModel(entry.imageModel === "core" ? "core" : "ultra");
        setArtStyle(entry.artStyle || "none");
        setArtStyleTail(null);
        setUseEditedPrompt(false);
        setCurrentImageUrl(entry.currentImageUrl || null);
        setRevisionImageUrl(entry.revisionImageUrl || null);
        setRevisionS3Key(entry.s3Key || null);
        setThemeKey(entry.themeKey || null);
        setStoryTags(Array.isArray(entry.tags) ? entry.tags : []);
        setThemeStyleTail(null);
        setError(null);
        setInfo(`Showing previous test for ${entry.storyId}`);
    };

    const handleClearHistory = () => {
        setHistory([]);
        writeHistory([]);
    };

    return (
        <Box sx={{ p: 2, pb: 6, maxWidth: 1200, mx: "auto" }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/admin/system/anything")}
                sx={{ mb: 2, textTransform: "none" }}
            >
                Back to Anything
            </Button>

            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {pageTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {pageSubtitle || (
                    <>
                        Compare current story art vs a new illustration in <code>story-revision/</code>. Use{" "}
                        <strong>v2</strong> for stored imagePrompt + theme pack (from template tags). Use{" "}
                        <strong>photo</strong> for the word-puzzle photo wrap, or{" "}
                        <strong>standalone v2</strong> for a sheet of 6 painted pictures (test only).
                        Production story images still use <strong>Core</strong> + v2 illustration; Ultra is for this page only.
                    </>
                )}
            </Typography>

            <Stack spacing={2} sx={{ mb: 3, maxWidth: 720 }}>
                <TextField
                    label="Story ID"
                    size="small"
                    value={storyId}
                    onChange={(e) => setStoryId(e.target.value)}
                    fullWidth
                    helperText={
                        <>
                            Default:{" "}
                            <Link
                                component={RouterLink}
                                to={`/admin/story/${TEST_PROMPT_V2_STORY_ID}`}
                            >
                                /admin/story/{TEST_PROMPT_V2_STORY_ID}
                            </Link>
                        </>
                    }
                />

                <FormControl>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Prompt version
                    </Typography>
                    <RadioGroup
                        row
                        value={promptVersion}
                        onChange={(e) => {
                            const next = e.target.value;
                            setPromptVersion(next);
                            if (next === "photo" && !PHOTO_ART_STYLE_KEYS.includes(artStyle)) {
                                setArtStyle("photorealistic");
                            }
                            if (next === "standalone-v2") {
                                setImageModel("core");
                            }
                        }}
                    >
                        <FormControlLabel
                            value="v1"
                            control={<Radio size="small" />}
                            label="v1 — stored / legacy derive"
                        />
                        <FormControlLabel
                            value="v2"
                            control={<Radio size="small" />}
                            label="v2 — hybrid + theme pack"
                        />
                        <FormControlLabel
                            value="photo"
                            control={<Radio size="small" />}
                            label="photo — standalone-like wrap"
                        />
                        <FormControlLabel
                            value="standalone-v2"
                            control={<Radio size="small" />}
                            label="standalone v2 — 6 painted pictures"
                        />
                    </RadioGroup>
                </FormControl>

                <FormControl>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Image model
                    </Typography>
                    <RadioGroup
                        row
                        value={imageModel}
                        onChange={(e) => setImageModel(e.target.value)}
                    >
                        <FormControlLabel
                            value="core"
                            control={<Radio size="small" />}
                            label="Core — production story model (default)"
                        />
                        <FormControlLabel
                            value="ultra"
                            control={<Radio size="small" />}
                            label="Ultra — Stable Image Ultra (ad quality)"
                        />
                    </RadioGroup>
                </FormControl>

                <FormControl size="small" fullWidth>
                    <InputLabel id="test-prompt-v2-art-style-label">Art style</InputLabel>
                    <Select
                        labelId="test-prompt-v2-art-style-label"
                        label="Art style"
                        value={promptVersion === "photo" && !PHOTO_ART_STYLE_KEYS.includes(artStyle)
                            ? "photorealistic"
                            : artStyle}
                        onChange={(e) => setArtStyle(e.target.value)}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                    >
                        {(promptVersion === "photo" ? PHOTO_ART_STYLE_OPTIONS : ART_STYLE_OPTIONS).map((opt) => (
                            <MenuItem key={opt.key} value={opt.key}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                        {promptVersion === "standalone-v2" ? (
                            <>
                                One square revision: exactly 6 paintings, 2 columns by 3 rows, same subject in
                                every panel. <strong>None</strong> uses the painted countryside look. Core is the
                                model for this option. Production gen-story is unchanged.
                            </>
                        ) : promptVersion === "photo" ? (
                            <>
                                Photo wrap uses <strong>Photorealistic</strong> or <strong>Cinematic</strong> only
                                (people/characters allowed). Production gen-story is unchanged.
                            </>
                        ) : (
                            <>
                                When set, this <strong>replaces</strong> the v2 theme pack (so “editorial
                                illustration” does not fight pencil/watercolor). The style is placed first in the
                                prompt. Choose <strong>None</strong> for normal theme-pack behavior. Test page
                                only — production image gen is unchanged.
                            </>
                        )}
                    </Typography>
                </FormControl>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={useEditedPrompt}
                            onChange={(e) => setUseEditedPrompt(e.target.checked)}
                            size="small"
                        />
                    }
                    label="Use edited prompt below (skip auto resolve / hybrid build)"
                />

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => loadStory(storyId)}
                        disabled={loadingStory || generating}
                        sx={{ textTransform: "none" }}
                    >
                        {loadingStory ? "Loading…" : "Load story"}
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={handleGenerate}
                        disabled={loadingStory || generating}
                        startIcon={generating ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                        {generating ? "Generating…" : "Generate revision"}
                    </Button>
                </Stack>

                <TextField
                    label="Image prompt"
                    value={prompt}
                    onChange={(e) => {
                        setPrompt(e.target.value);
                        setUseEditedPrompt(true);
                    }}
                    multiline
                    minRows={6}
                    fullWidth
                    disabled={generating}
                    helperText={
                        promptVersion === "standalone-v2"
                            ? "The story scene is painted as a 2×3 sheet of 6 pictures. After generate, this box shows the exact prompt that was sent."
                            : useEditedPrompt
                            ? artStyle !== "none"
                                ? "Edited scene will be wrapped with the selected art style (style-first)."
                                : "Edited prompt will be sent as-is to Stable Image."
                            : artStyle !== "none"
                              ? "Auto-build uses story scene only + selected art style (theme pack skipped). After generate, this box shows the exact prompt that was sent."
                              : promptVersion === "v2"
                                ? "v2 keeps the story imagePrompt and appends the theme style pack from tags."
                                : "v1 will use stored imagePrompt (or legacy derive)."
                    }
                />

                {themeKey || storyTags.length > 0 || (artStyle && artStyle !== "none") ? (
                    <Box>
                        {themeKey || storyTags.length > 0 ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                                Resolved theme: <strong>{themeKey || "—"}</strong>
                                {storyTags.length > 0 ? ` · tags: ${storyTags.join(", ")}` : ""}
                            </Typography>
                        ) : null}
                        {themeStyleTail ? (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Theme style tail: {themeStyleTail}
                            </Typography>
                        ) : null}
                        {artStyle && artStyle !== "none" ? (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Art style: <strong>{artStyleLabel(artStyle)}</strong>
                                {artStyleTail ? ` — ${artStyleTail}` : ""}
                            </Typography>
                        ) : null}
                    </Box>
                ) : null}
            </Stack>

            {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}
            {info ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {info}
                    {revisionS3Key ? (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            S3 key: {revisionS3Key}
                        </Typography>
                    ) : null}
                </Alert>
            ) : null}

            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "flex-start",
                }}
            >
                <ComparePane
                    label="Current story image"
                    url={currentImageUrl}
                    emptyHint="Load a story to see the current illustration."
                />
                <ComparePane
                    label="Updated (story-revision/)"
                    url={revisionImageUrl}
                    emptyHint="Click Generate revision to create a new image."
                />
            </Box>

            <Box sx={{ mt: 4 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Previous tests ({history.length})
                    </Typography>
                    {history.length > 0 ? (
                        <Button size="small" onClick={handleClearHistory} sx={{ textTransform: "none" }}>
                            Clear history
                        </Button>
                    ) : null}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Saved in this browser after each generate. Click a row to show that current vs
                    revision again.
                </Typography>
                {history.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No previous tests yet — generate a revision to start the list.
                    </Typography>
                ) : (
                    <Stack spacing={1}>
                        {history.map((entry) => (
                            <Box
                                key={entry.id}
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "center",
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    p: 1,
                                    cursor: "pointer",
                                    "&:hover": { bgcolor: "action.hover" },
                                }}
                                onClick={() => handleOpenHistory(entry)}
                            >
                                <Box
                                    component="img"
                                    src={entry.revisionImageUrl}
                                    alt=""
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        objectFit: "cover",
                                        borderRadius: 0.5,
                                        flexShrink: 0,
                                        bgcolor: "action.hover",
                                    }}
                                />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {entry.storyId}
                                        {entry.themeKey ? ` · ${entry.themeKey}` : ""}
                                        {entry.promptVersion ? ` · ${entry.promptVersion}` : ""}
                                        {entry.imageModel ? ` · ${entry.imageModel}` : ""}
                                        {entry.artStyle && entry.artStyle !== "none"
                                            ? ` · ${artStyleLabel(entry.artStyle)}`
                                            : ""}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {entry.at
                                            ? new Date(entry.at).toLocaleString()
                                            : ""}
                                        {entry.s3Key ? ` · ${entry.s3Key}` : ""}
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    href={entry.revisionImageUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ textTransform: "none", flexShrink: 0 }}
                                >
                                    Open
                                </Button>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        </Box>
    );
}
